import React, { createContext, useContext, useState, useEffect } from 'react';

const ToolBoxContext = createContext();

export const useToolBox = () => {
  const context = useContext(ToolBoxContext);
  if (!context) {
    throw new Error('useToolBox must be used within a ToolBoxProvider');
  }
  return context;
};

export const ToolBoxProvider = ({ children }) => {
  const [doctorScheduleState, setDoctorScheduleState] = useState(null);
  const [qaKnowledgeBaseState, setQAKnowledgeBaseState] = useState(null);
  const [isProcessing, setIsProcessing] = useState({
    doctorSchedule: false,
    qaKnowledgeBase: false
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Load state from sessionStorage asynchronously to avoid blocking
  // Only load when actually needed (lazy loading)
  useEffect(() => {
    // Check if we're on a ToolBox page - if not, skip loading
    const isToolBoxPage = window.location.pathname.includes('/toolbox');
    if (!isToolBoxPage) {
      setIsInitialized(true);
      return;
    }

    // Use requestIdleCallback or setTimeout to defer loading (non-blocking)
    const loadState = () => {
      try {
        // Use try-catch for each operation to prevent one failure from blocking others
        const loadDoctorSchedule = () => {
          try {
            const saved = sessionStorage.getItem('toolbox_doctor_schedule');
            if (saved) {
              const parsed = JSON.parse(saved);
              setDoctorScheduleState(parsed);
            }
          } catch (e) {
            console.error('Error loading doctor schedule state:', e);
            sessionStorage.removeItem('toolbox_doctor_schedule');
          }
        };

        const loadQAKnowledgeBase = () => {
          try {
            const saved = sessionStorage.getItem('toolbox_qa_knowledge_base');
            if (saved) {
              const parsed = JSON.parse(saved);
              setQAKnowledgeBaseState(parsed);
            }
          } catch (e) {
            console.error('Error loading QA knowledge base state:', e);
            sessionStorage.removeItem('toolbox_qa_knowledge_base');
          }
        };

        // Load states in separate microtasks to avoid blocking
        Promise.resolve().then(() => {
          loadDoctorSchedule();
          return Promise.resolve();
        }).then(() => {
          loadQAKnowledgeBase();
        }).finally(() => {
          setIsInitialized(true);
        });
      } catch (e) {
        console.error('Error loading ToolBox state:', e);
        setIsInitialized(true);
      }
    };

    // Use requestIdleCallback if available for true non-blocking, otherwise use setTimeout
    if (window.requestIdleCallback) {
      requestIdleCallback(loadState, { timeout: 2000 });
    } else {
      // Use multiple setTimeout calls to break up the work
      setTimeout(loadState, 0);
    }
  }, []);

  // Save doctor schedule state to sessionStorage with debouncing to avoid blocking
  useEffect(() => {
    if (!doctorScheduleState) {
      sessionStorage.removeItem('toolbox_doctor_schedule');
      return;
    }

    // Debounce sessionStorage writes to avoid blocking
    const timeoutId = setTimeout(() => {
      try {
        sessionStorage.setItem('toolbox_doctor_schedule', JSON.stringify(doctorScheduleState));
      } catch (e) {
        console.error('Error saving doctor schedule state:', e);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [doctorScheduleState]);

  // Save QA knowledge base state to sessionStorage with debouncing to avoid blocking
  useEffect(() => {
    if (!qaKnowledgeBaseState) {
      sessionStorage.removeItem('toolbox_qa_knowledge_base');
      return;
    }

    // Debounce sessionStorage writes to avoid blocking
    const timeoutId = setTimeout(() => {
      try {
        sessionStorage.setItem('toolbox_qa_knowledge_base', JSON.stringify(qaKnowledgeBaseState));
      } catch (e) {
        console.error('Error saving QA knowledge base state:', e);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [qaKnowledgeBaseState]);

  const updateDoctorScheduleState = (state) => {
    setDoctorScheduleState(state);
  };

  const updateQAKnowledgeBaseState = (state) => {
    setQAKnowledgeBaseState(state);
  };

  const clearDoctorScheduleState = () => {
    setDoctorScheduleState(null);
    sessionStorage.removeItem('toolbox_doctor_schedule');
  };

  const clearQAKnowledgeBaseState = () => {
    setQAKnowledgeBaseState(null);
    sessionStorage.removeItem('toolbox_qa_knowledge_base');
  };

  const setProcessing = (tool, processing) => {
    setIsProcessing(prev => ({
      ...prev,
      [tool]: processing
    }));
  };

  const value = {
    doctorScheduleState,
    qaKnowledgeBaseState,
    updateDoctorScheduleState,
    updateQAKnowledgeBaseState,
    clearDoctorScheduleState,
    clearQAKnowledgeBaseState,
    isProcessing,
    setProcessing,
  };

  return (
    <ToolBoxContext.Provider value={value}>
      {children}
    </ToolBoxContext.Provider>
  );
};

